import React, { useEffect, useMemo, useRef, useState } from 'react';

// BL-119 — lightweight logo cropper. Operates on the locally-selected File (a
// same-origin object URL) so the canvas never gets tainted by cross-origin S3
// images — the cropped PNG blob is then uploaded like any other logo. No crop
// library needed: a draggable/resizable box over a fit-scaled preview, mapped
// back to the image's natural resolution on apply.
const MAX_W = 380;
const MAX_H = 260;
const MIN = 24;

const LogoCropper = ({ file, onApply, onCancel }) => {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  const imgRef = useRef(null);
  const drag = useRef(null);
  const [disp, setDisp] = useState(null); // displayed size {w,h}
  const [nat, setNat] = useState(null);   // natural size {w,h}
  const [rect, setRect] = useState(null); // crop box in displayed px

  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  const onImgLoad = (e) => {
    const iw = e.target.naturalWidth;
    const ih = e.target.naturalHeight;
    const scale = Math.min(MAX_W / iw, MAX_H / ih, 1);
    const w = Math.max(1, Math.round(iw * scale));
    const h = Math.max(1, Math.round(ih * scale));
    setNat({ w: iw, h: ih });
    setDisp({ w, h });
    setRect({ x: 0, y: 0, w, h }); // default = whole image
  };

  const clamp = (r) => {
    let { x, y, w, h } = r;
    w = Math.min(Math.max(MIN, w), disp.w);
    h = Math.min(Math.max(MIN, h), disp.h);
    x = Math.min(Math.max(0, x), disp.w - w);
    y = Math.min(Math.max(0, y), disp.h - h);
    return { x, y, w, h };
  };

  useEffect(() => {
    const move = (e) => {
      if (!drag.current || !disp) return;
      const dx = e.clientX - drag.current.startX;
      const dy = e.clientY - drag.current.startY;
      const r0 = drag.current.rect;
      if (drag.current.mode === 'move') {
        setRect(clamp({ ...r0, x: r0.x + dx, y: r0.y + dy }));
      } else {
        setRect(clamp({ ...r0, w: r0.w + dx, h: r0.h + dy }));
      }
    };
    const up = () => { drag.current = null; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disp]);

  const startDrag = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { mode, startX: e.clientX, startY: e.clientY, rect };
  };

  const apply = () => {
    const imgEl = imgRef.current;
    if (!imgEl || !rect || !nat || !disp) return;
    const sx = (rect.x * nat.w) / disp.w;
    const sy = (rect.y * nat.h) / disp.h;
    const sw = (rect.w * nat.w) / disp.w;
    const sh = (rect.h * nat.h) / disp.h;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sw));
    canvas.height = Math.max(1, Math.round(sh));
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => { if (blob) onApply(blob); }, 'image/png');
  };

  return (
    <div className="doc-crop-overlay" onClick={(e) => { e.stopPropagation(); onCancel(); }}>
      <div className="doc-crop" onClick={(e) => e.stopPropagation()}>
        <div className="doc-crop__head">Crop logo</div>
        <div className="doc-crop__stagewrap">
          <div className="doc-crop__stage" style={disp ? { width: disp.w, height: disp.h } : undefined}>
            <img ref={imgRef} className="doc-crop__img" src={url} alt="" onLoad={onImgLoad} draggable={false} />
            {rect && (
              <div
                className="doc-crop__box"
                style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
                onPointerDown={startDrag('move')}
              >
                <span className="doc-crop__handle" onPointerDown={startDrag('resize')} />
              </div>
            )}
          </div>
        </div>
        <div className="doc-crop__hint">Drag the box to move it; drag the corner to resize.</div>
        <div className="doc-crop__foot">
          <button type="button" className="doc-crop__ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="doc-crop__ghost" onClick={() => onApply(file)}>Use original</button>
          <button type="button" className="doc-btn doc-btn--primary" onClick={apply}>Apply crop</button>
        </div>
      </div>
    </div>
  );
};

export default LogoCropper;
